import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { format } from "date-fns";
import Plot from "react-plotly.js";
import { toast } from "react-toastify";

import {
  supportGetErrors,
  supportGetErrorStats,
} from "src/apiService/modules/support";
import type {
  ClientErrorRecord,
  ErrorStats,
} from "src/interfaces/errorStats.interface";
import ClientErrorsTable from "src/pages/support/ClientErrorsTable";
import SupportDateRangeFilter from "src/pages/support/SupportDateRangeFilter";
import {
  DayMs,
  rangeForSupportDatePreset,
  type SupportDateRangePresetId,
} from "src/pages/support/dateRangePresets";
import { setSupportLoginToken } from "src/store/support/actions";
import { supportGetToken } from "src/store/support/selector";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    margin: theme.spacing(2),
  },
  chartWrapper: {
    width: "100%",
    marginBottom: theme.spacing(3),
  },
  chart: {
    width: "100%",
    minHeight: 320,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
}));

function formatBucketLabel(
  bucket: ErrorStats["buckets"][number],
  bucketMs: number
) {
  const start = new Date(bucket.start);
  if (bucketMs <= DayMs) return format(start, "MMM d, HH:mm");
  return format(start, "MMM d");
}

function Errors() {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const token = useSelector(supportGetToken);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [stats, setStats] = useState<ErrorStats | undefined>();
  const [errors, setErrors] = useState<ClientErrorRecord[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [activePreset, setActivePreset] =
    useState<SupportDateRangePresetId | null>("1d");
  const [from, setFrom] = useState(() => new Date(Date.now() - DayMs));
  const [to, setTo] = useState(() => new Date());

  const fetchData = useCallback(
    async (fromDate: Date, toDate: Date, cursor?: string) => {
      if (!token) return;

      if (cursor) setLoadingMore(true);
      else setLoading(true);

      try {
        const fromIso = fromDate.toISOString();
        const toIso = toDate.toISOString();

        const [statsResult, errorsResult] = await Promise.all([
          cursor
            ? Promise.resolve(null)
            : supportGetErrorStats(token, fromIso, toIso),
          supportGetErrors(
            token,
            fromIso,
            toIso,
            cursor ? { limit: 25, cursor } : { limit: 25 }
          ),
        ]);

        if (statsResult) setStats(statsResult.data);
        setErrors((prev) =>
          cursor
            ? [...prev, ...errorsResult.data.errors]
            : errorsResult.data.errors
        );
        setNextCursor(errorsResult.data.nextCursor);
      } catch (err: any) {
        if (err?.code === "failed-precondition") {
          dispatch(setSupportLoginToken());
        }
        if (!cursor) {
          setStats(undefined);
          setErrors([]);
          setNextCursor(null);
        }
        toast.error(err?.message || "Failed to load errors");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [dispatch, token]
  );

  const applyPreset = useCallback(
    (presetId: SupportDateRangePresetId, fromDate: Date, toDate: Date) => {
      setActivePreset(presetId);
      setFrom(fromDate);
      setTo(toDate);
      void fetchData(fromDate, toDate);
    },
    [fetchData]
  );

  useEffect(() => {
    if (!token) return;
    const range = rangeForSupportDatePreset("1d");
    applyPreset("1d", range.from, range.to);
  }, [token, applyPreset]);

  const plot = useMemo(() => {
    if (!stats?.buckets.length) return null;

    const x = stats.buckets.map((bucket) =>
      formatBucketLabel(bucket, stats.bucketMs)
    );

    return (
      <Box className={classes.chartWrapper}>
        <Plot
          className={classes.chart}
          useResizeHandler
          style={{ width: "100%", height: "100%" }}
          data={[
            {
              x,
              y: stats.counts,
              type: "scatter",
              mode: "lines+markers",
              name: "Errors",
              line: { color: theme.palette.error.main, width: 2 },
              marker: { color: theme.palette.error.main },
              hovertemplate: "%{x}<br />%{y} errors<extra></extra>",
            },
          ]}
          revision={stats.buckets.length}
          config={{
            displaylogo: false,
            displayModeBar: false,
            responsive: true,
          }}
          layout={{
            autosize: true,
            height: 320,
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            font: { color: theme.palette.text.primary },
            margin: { l: 48, r: 16, t: 24, b: 64 },
            xaxis: { title: "Time" },
            yaxis: { title: "Error count" },
          }}
        />
      </Box>
    );
  }, [
    stats,
    classes.chart,
    classes.chartWrapper,
    theme.palette.error.main,
    theme.palette.text.primary,
  ]);

  return (
    <Box component={Paper} className={classes.paper}>
      <Typography variant="h6" gutterBottom>
        Client errors
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Unhandled client errors reported from the main app (not support pages).
        Errors recorded during accountant impersonation include the accountant
        username when available (resolved from the `accountant` support uid).
      </Typography>

      <SupportDateRangeFilter
        from={from}
        to={to}
        activePreset={activePreset}
        onPresetChange={applyPreset}
        onFromChange={setFrom}
        onToChange={setTo}
        onApply={() => {
          setActivePreset(null);
          void fetchData(from, to);
        }}
      />

      {loading ? (
        <Box className={classes.loading}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {plot}
          <ClientErrorsTable
            errors={errors}
            showUser
            hasMore={!!nextCursor}
            loadingMore={loadingMore}
            onLoadMore={() =>
              nextCursor && void fetchData(from, to, nextCursor)
            }
          />
        </>
      )}
    </Box>
  );
}

export default Errors;
